import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  Home,
  Film,
  Zap,
  Lightbulb,
  Sparkles,
  LayoutGrid,
  GraduationCap,
  FolderKanban,
  History,
  Search,
  Menu,
  X,
  ExternalLink,
  ChevronRight,
  RotateCcw,
} from "lucide-react";

interface VideosLayoutProps {
  children: React.ReactNode;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  searchValue?: string;
  onSearchChange?: (val: string) => void;
}

export const VideosLayout: React.FC<VideosLayoutProps> = ({
  children,
  loading = false,
  error = false,
  onRetry,
  searchValue = "",
  onSearchChange,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [query, setQuery] = useState(searchValue);

  useEffect(() => {
    setQuery(searchValue);
  }, [searchValue]);

  // Close drawer on ESC
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && mobileDrawerOpen) {
        setMobileDrawerOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [mobileDrawerOpen]);

  // Close drawer on location change
  useEffect(() => {
    setMobileDrawerOpen(false);
  }, [location.pathname]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/videos/search?q=${encodeURIComponent(query.trim())}`);
    }
  };

  const isNavActive = (path: string) => {
    if (path === "/videos") {
      return location.pathname === "/videos";
    }
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  const navGroups = [
    {
      items: [
        { label: "Home", href: "/videos", icon: Home },
        { label: "All videos", href: "/videos/all", icon: Film },
        { label: "Shorts", href: "/videos/shorts", icon: Zap },
      ],
    },
    {
      title: "CATEGORIES",
      items: [
        { label: "Tips", href: "/videos/c/tips", icon: Lightbulb },
        {
          label: "Before & After",
          href: "/videos/c/before-after",
          icon: Sparkles,
        },
        { label: "Setups", href: "/videos/c/setups", icon: LayoutGrid },
      ],
    },
    {
      title: "LEARN",
      items: [
        {
          label: "Academy",
          href: "/videos/academy",
          icon: GraduationCap,
          badge: "Soon",
        },
        {
          label: "Courses & playlists",
          href: "/videos/playlists",
          icon: FolderKanban,
        },
      ],
    },
    {
      title: "YOU",
      items: [
        { label: "Continue watching", href: "/videos/history", icon: History },
      ],
    },
  ];

  const renderNavItems = (isIconOnly = false) => (
    <nav className="space-y-6" aria-label="Videos Navigation">
      {navGroups.map((group, idx) => (
        <div key={idx} className="space-y-1">
          {group.title && !isIconOnly && (
            <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">
              {group.title}
            </div>
          )}
          {group.items.map((item) => {
            const active = isNavActive(item.href);
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                to={item.href}
                aria-current={active ? "page" : undefined}
                className={`flex items-center ${
                  isIconOnly ? "justify-center px-2" : "justify-between px-3"
                } py-2.5 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#D97706] ${
                  active
                    ? "bg-[#E8850F]/14 text-[#9A5207] font-semibold"
                    : "text-foreground/80 hover:bg-[#E8850F]/10 hover:text-[#9A5207]"
                }`}
                title={isIconOnly ? item.label : undefined}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Icon
                    className={`w-4 h-4 shrink-0 ${
                      active ? "text-[#9A5207]" : "text-muted-foreground"
                    }`}
                  />
                  {!isIconOnly && (
                    <span className="truncate">{item.label}</span>
                  )}
                </div>
                {!isIconOnly && item.badge && (
                  <span className="px-1.5 py-0.5 text-[10px] font-semibold tracking-wide rounded bg-[#E8850F]/20 text-[#9A5207]">
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-900 font-body">
      <Navbar />

      <div className="flex-1 container mx-auto px-4 py-6">
        <div className="flex gap-8">
          {/* Desktop Sidebar (>= 1180px) */}
          <aside className="hidden min-[1180px]:block w-[240px] shrink-0 sticky top-24 h-[calc(100vh-7rem)] overflow-y-auto pr-2 border-r border-slate-100">
            {renderNavItems(false)}
          </aside>

          {/* Tablet Icon Rail (768px - 1179px) */}
          <aside className="hidden md:block min-[1180px]:hidden w-[76px] shrink-0 sticky top-24 h-[calc(100vh-7rem)] overflow-y-auto border-r border-slate-100">
            {renderNavItems(true)}
          </aside>

          {/* Main Content Area */}
          <main className="flex-1 min-w-0 pb-20 md:pb-12">
            {/* Search Header */}
            <div className="mb-6 flex items-center gap-3">
              {/* Mobile Drawer Toggle */}
              <button
                onClick={() => setMobileDrawerOpen(true)}
                className="md:hidden p-2.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                aria-label="Open video navigation"
              >
                <Menu className="w-5 h-5" />
              </button>

              <form
                onSubmit={handleSearchSubmit}
                className="relative flex-1 max-w-xl"
                role="search"
              >
                <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    if (onSearchChange) onSearchChange(e.target.value);
                  }}
                  placeholder="Search videos, categories, playlists..."
                  aria-label="Search videos"
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm placeholder:text-slate-400 text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#D97706] focus:bg-white focus:border-transparent transition-all"
                />
              </form>
            </div>

            {/* Error or Loading fallback banner if error */}
            {error && (
              <div
                role="alert"
                className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 flex flex-col sm:flex-row items-center justify-between gap-4"
              >
                <p className="text-sm font-medium">
                  Videos are loading slowly. Try again
                </p>
                <div className="flex items-center gap-3">
                  {onRetry && (
                    <button
                      onClick={onRetry}
                      className="px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition-colors flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                    >
                      <RotateCcw className="w-3.5 h-3.5" /> Retry
                    </button>
                  )}
                  <a
                    href="https://www.youtube.com/@JNNHomes"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-semibold text-amber-900 hover:underline flex items-center gap-1"
                  >
                    YouTube Channel <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            )}

            {/* Skeleton while loading */}
            {loading ? (
              <div
                className="space-y-8 animate-pulse"
                aria-busy="true"
                aria-label="Loading content"
              >
                <div className="h-8 w-64 bg-slate-200 rounded-lg" />
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {[...Array(10)].map((_, i) => (
                    <div key={i} className="space-y-3">
                      <div className="aspect-[9/16] bg-slate-200 rounded-2xl" />
                      <div className="h-4 bg-slate-200 rounded w-5/6" />
                      <div className="h-3 bg-slate-200 rounded w-1/2" />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              children
            )}

            {/* End of page CTA Card */}
            <div className="mt-14 p-6 rounded-2xl bg-slate-50 border border-slate-200 border-l-4 border-l-[#E8850F] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm">
              <div>
                <h3 className="text-base font-bold text-slate-900">
                  Own a home in Northern Virginia?
                </h3>
                <p className="text-sm text-slate-600 mt-0.5">
                  See how JNN Homes can care for it with full-service Airbnb
                  management.
                </p>
              </div>
              <a
                href="/#lead-form"
                onClick={(e) => {
                  if (window.location.pathname === "/") {
                    e.preventDefault();
                    document
                      .getElementById("lead-form")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                className="shrink-0 px-5 py-2.5 rounded-xl font-bold text-sm bg-[#B35A06] text-white hover:bg-[#9A5207] focus:outline-none focus:ring-2 focus:ring-[#D97706] transition-colors shadow-sm inline-flex items-center gap-2"
              >
                Talk to JNN Homes <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          </main>
        </div>
      </div>

      {/* Mobile Navigation Drawer */}
      {mobileDrawerOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs transition-opacity"
            onClick={() => setMobileDrawerOpen(false)}
            aria-hidden="true"
          />
          {/* Drawer */}
          <div className="fixed inset-y-0 left-0 w-[280px] bg-white shadow-2xl p-6 overflow-y-auto flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-6">
                <span className="font-bold text-slate-900 text-lg">
                  Videos Navigation
                </span>
                <button
                  onClick={() => setMobileDrawerOpen(false)}
                  className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                  aria-label="Close menu"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              {renderNavItems(false)}
            </div>
            <div className="pt-6 border-t border-slate-100 mt-6">
              <a
                href="https://www.youtube.com/@JNNHomes?sub_confirmation=1"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-2.5 px-4 rounded-xl bg-slate-900 text-white font-bold text-xs flex items-center justify-center gap-2"
              >
                Subscribe on YouTube
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Fixed Bottom Nav Bar (< 768px) */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200 px-2 py-1.5 flex items-center justify-around shadow-lg"
        aria-label="Mobile Videos Bottom Bar"
      >
        {[
          { label: "Home", href: "/videos", icon: Home },
          { label: "Categories", href: "/videos/c/tips", icon: LayoutGrid },
          { label: "Courses", href: "/videos/playlists", icon: FolderKanban },
          { label: "Academy", href: "/videos/academy", icon: GraduationCap },
          { label: "History", href: "/videos/history", icon: History },
        ].map((tab) => {
          const active = isNavActive(tab.href);
          const Icon = tab.icon;
          return (
            <Link
              key={tab.href}
              to={tab.href}
              aria-current={active ? "page" : undefined}
              className={`flex flex-col items-center py-1 px-2 rounded-lg text-[10px] font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-[#D97706] ${
                active
                  ? "text-[#9A5207]"
                  : "text-slate-500 hover:text-slate-900"
              }`}
            >
              <Icon className="w-4 h-4 mb-0.5" />
              <span>{tab.label}</span>
            </Link>
          );
        })}
      </nav>

      <Footer />
    </div>
  );
};
